// Sponsorship CRM: profile, campaigns, sponsor companies and contacts,
// the deal pipeline, outreach messages, and the AI agent that finds
// sponsors and drafts outreach for you to approve.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DealStage } from '@podcastwiz-app/shared';
import { api } from './api';

export interface SponsorProfile {
  podcastName: string;
  podcastDescription: string | null;
  niche: string | null;
  audienceSize: number | null;
  cpmRange: { min: number; max: number } | null;
  preferredCategories: string[] | null;
  onboardingCompleted: boolean;
}

export interface Campaign {
  id: string;
  name: string;
  status: string;
  goal: string | null;
}

export interface Company {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
  description: string | null;
  estimatedBudget: string | null;
  source: string | null;
  aiRelevanceScore: number | null;
  createdAt: string;
}

export interface Contact {
  id: string;
  companyId: string;
  name: string;
  email: string | null;
  role: string | null;
  isPrimary: boolean;
}

export interface Deal {
  id: string;
  campaignId: string;
  companyId: string;
  contactId: string | null;
  stage: DealStage;
  dealValueCents: number | null;
  currency: string | null;
  notes: string | null;
  lastActivityAt: string | null;
  createdAt: string;
}

export interface Message {
  id: string;
  direction: 'outbound' | 'inbound';
  subject: string | null;
  body: string | null;
  status: string;
  aiGenerated: boolean;
  sentAt: string | null;
  createdAt: string;
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

// ── Pipeline columns ──

export interface Column {
  key: string;
  label: string;
  stages: DealStage[];
}

/** Eleven stages, five columns: enough detail on the card, a board that fits. */
export const COLUMNS: Column[] = [
  { key: 'prospects', label: 'Prospects', stages: ['discovered', 'researching'] },
  { key: 'contacted', label: 'Contacted', stages: ['contacted'] },
  { key: 'talking', label: 'In talks', stages: ['replied', 'negotiating', 'verbal_yes'] },
  { key: 'won', label: 'Won', stages: ['signed', 'live', 'completed', 'closed_won'] },
  { key: 'lost', label: 'Lost', stages: ['closed_lost'] },
];

export const STAGE_LABELS: Record<DealStage, string> = {
  discovered: 'Discovered',
  researching: 'Researching',
  contacted: 'Contacted',
  replied: 'Replied',
  negotiating: 'Negotiating',
  verbal_yes: 'Verbal yes',
  signed: 'Signed',
  live: 'Live',
  completed: 'Completed',
  closed_won: 'Won',
  closed_lost: 'Lost',
};

export function money(cents: number | null, currency: string | null = 'USD'): string {
  if (cents === null) return '';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency ?? 'USD',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** Total value of the deals in a column (currency of the first deal). */
export function columnTotal(deals: Deal[]): string {
  const cents = deals.reduce((n, d) => n + (d.dealValueCents ?? 0), 0);
  return cents ? money(cents, deals.find((d) => d.currency)?.currency ?? 'USD') : '';
}

// ── Queries ──

export function useSponsorProfile() {
  return useQuery<SponsorProfile | null>({
    queryKey: ['sponsor-profile'],
    queryFn: async () => (await api<{ profile: SponsorProfile | null }>('/api/sponsorship/profile')).profile,
  });
}

export function useSaveProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profile: Partial<SponsorProfile> & { podcastName: string }) =>
      api('/api/sponsorship/profile', { method: 'PUT', ...json(profile) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sponsor-profile'] }),
  });
}

export function useCampaigns() {
  return useQuery<Campaign[]>({
    queryKey: ['campaigns'],
    queryFn: async () => (await api<{ campaigns: Campaign[] }>('/api/sponsorship/campaigns')).campaigns,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; goal?: string }) =>
      (await api<{ campaign: Campaign }>('/api/sponsorship/campaigns', { method: 'POST', ...json(input) })).campaign,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ deleted: boolean; deals: number }>(`/api/sponsorship/campaigns/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      void qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useCompanies() {
  return useQuery<Company[]>({
    queryKey: ['companies'],
    queryFn: async () => (await api<{ companies: Company[] }>('/api/sponsorship/companies?limit=100')).companies,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; domain?: string; industry?: string }) =>
      (await api<{ company: Company }>('/api/sponsorship/companies', { method: 'POST', ...json(input) })).company,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });
}

export function useContacts(companyId: string | undefined) {
  return useQuery<Contact[]>({
    queryKey: ['contacts', companyId],
    enabled: !!companyId,
    queryFn: async () => (await api<{ contacts: Contact[] }>(`/api/sponsorship/companies/${companyId}/contacts`)).contacts,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { companyId: string; name: string; email?: string; role?: string }) =>
      (await api<{ contact: Contact }>('/api/sponsorship/contacts', { method: 'POST', ...json({ ...input, isPrimary: true }) }))
        .contact,
    onSuccess: (c) => qc.invalidateQueries({ queryKey: ['contacts', c.companyId] }),
  });
}

export function useDeals() {
  return useQuery<Deal[]>({
    queryKey: ['deals'],
    queryFn: async () => (await api<{ deals: Deal[] }>('/api/sponsorship/deals')).deals,
  });
}

export function useCreateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { campaignId: string; companyId: string }) =>
      (await api<{ deal: Deal }>('/api/sponsorship/deals', { method: 'POST', ...json(input) })).deal,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deals'] }),
  });
}

export function useUpdateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; dealValueCents?: number; notes?: string; contactId?: string }) =>
      api(`/api/sponsorship/deals/${id}`, { method: 'PATCH', ...json(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deals'] }),
  });
}

export function useMoveDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: DealStage }) =>
      api(`/api/sponsorship/deals/${id}/move-stage`, { method: 'POST', ...json({ stage }) }),
    // Move the card at once; the server confirms.
    onMutate: ({ id, stage }) => {
      qc.setQueryData<Deal[]>(['deals'], (deals) => deals?.map((d) => (d.id === id ? { ...d, stage } : d)));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['deals'] }),
  });
}

export function useDealMessages(dealId: string | undefined, poll = false) {
  return useQuery<Message[]>({
    queryKey: ['deal-messages', dealId],
    enabled: !!dealId,
    queryFn: async () => (await api<{ messages: Message[] }>(`/api/sponsorship/deals/${dealId}/messages`)).messages,
    refetchInterval: poll ? 3000 : false,
  });
}

function useMessageMutation<T>(fn: (input: T) => Promise<unknown>, dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deal-messages', dealId] }),
  });
}

export function useEditMessage(dealId: string) {
  return useMessageMutation(
    ({ id, subject, body }: { id: string; subject: string; body: string }) =>
      api(`/api/sponsorship/messages/${id}`, { method: 'PATCH', ...json({ subject, body }) }),
    dealId,
  );
}

export function useDiscardMessage(dealId: string) {
  return useMessageMutation((id: string) => api(`/api/sponsorship/messages/${id}`, { method: 'DELETE' }), dealId);
}

export function useSendMessage(dealId: string) {
  return useMessageMutation(
    (id: string) => api<{ jobId: string }>(`/api/sponsorship/messages/${id}/send`, { method: 'POST' }),
    dealId,
  );
}

export type AgentTask = 'discover' | 'generate-outreach' | 'campaign-review' | 'agent-chat';

/** Start an agent task; returns the job to follow. */
export function useRunAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { type: AgentTask; campaignId?: string; dealId?: string; message?: string }) =>
      api<{ jobId: string }>('/api/sponsorship/agent/run', { method: 'POST', ...json(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  });
}

export const MESSAGE_STATUS: Record<string, string> = {
  pending_approval: 'Needs your approval',
  approved: 'Approved',
  draft: 'Draft',
  sent: 'Sent',
  received: 'Received',
  failed: 'Failed to send',
};

// ── Assistant chat ──

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export function useChat() {
  return useQuery<ChatMessage[]>({
    queryKey: ['sponsor-chat'],
    queryFn: async () => (await api<{ messages: ChatMessage[] }>('/api/sponsorship/chat')).messages,
  });
}

export function useClearChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api('/api/sponsorship/chat', { method: 'DELETE' }),
    onSuccess: () => qc.setQueryData(['sponsor-chat'], []),
  });
}

// ── Autonomy ──

export interface Autonomy {
  autonomyLevel: string;
  dailyOutreachLimit: number | null;
  preferredTone: string | null;
}

/**
 * The modes the agent really implements. ("Approve the first" and "fully
 * automatic" exist in the schema, but nothing sends approved drafts on
 * its own yet, so they aren't offered.)
 */
export const AUTONOMY_MODES = [
  {
    value: 'approve_each',
    label: 'Draft emails for my approval',
    description: 'The AI researches sponsors and writes pitches. Nothing is sent until you approve it.',
  },
  {
    value: 'ai_coach_mode',
    label: 'Research and advice only',
    description: 'The AI researches and advises, but doesn’t save sponsors or draft emails.',
  },
] as const;

export const TONES = ['professional', 'friendly', 'casual', 'enthusiastic'] as const;

export function useAutonomy() {
  return useQuery<Autonomy | null>({
    queryKey: ['sponsor-autonomy'],
    queryFn: async () => (await api<{ autonomy: Autonomy | null }>('/api/sponsorship/autonomy')).autonomy,
  });
}

export function useSaveAutonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Autonomy>) => api('/api/sponsorship/autonomy', { method: 'PUT', ...json(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sponsor-autonomy'] }),
  });
}
